import { Decimal } from 'decimal.js';
import moment from 'moment';

export const OBLIGATION_TYPE = {
  COHORT_RETURN: 'cohort_return',
  FACILITY_FEE: 'facility_fee',
  // A free-text charge the user adds to the invoice (legal costs, say): just a
  // description and an amount. No obligation stands behind it, so no repayment
  // settles it — the set-off covers it, or it is marked paid on the invoice.
  // Must match OtherChargeLineType in customer-service.
  OTHER_CHARGE: 'other_charge',
};

// The line types a repayment settles. An invoice made up only of other charges
// has nothing for the repayment screen to allocate against, so it is marked
// paid directly instead — see hasObligationLines in customer-service, which
// makes the same split server-side.
export const OBLIGATION_LINE_TYPES = [
  OBLIGATION_TYPE.COHORT_RETURN,
  OBLIGATION_TYPE.FACILITY_FEE,
];

export function isObligationLine(line) {
  return OBLIGATION_LINE_TYPES.includes(line?.type);
}

export function isOtherChargeLine(line) {
  return line?.type === OBLIGATION_TYPE.OTHER_CHARGE;
}

export function hasObligationLines(lines = []) {
  return lines.some((line) => line.included && isObligationLine(line));
}

export function hasOtherChargeLines(lines = []) {
  return lines.some((line) => line.included && isOtherChargeLine(line));
}

// What the invoice bills beyond its obligations. A repayment never covers this
// slice, so the repayment screen subtracts it from both the amount and the
// set-off it offers.
export function sumOtherChargeAmounts(lines = []) {
  return sumIncludedLineAmounts(lines.filter(isOtherChargeLine));
}

// What a repayment can actually settle: cohort returns and facility fees only.
export function sumObligationAmounts(lines = []) {
  return sumIncludedLineAmounts(lines.filter(isObligationLine));
}

let otherChargeSequence = 0;

export function createOtherChargeLine() {
  otherChargeSequence += 1;
  return {
    id: `charge-${Date.now()}-${otherChargeSequence}`,
    type: OBLIGATION_TYPE.OTHER_CHARGE,
    included: true,
    description: '',
    amount: null,
    dueDate: null,
  };
}

// A charge is only complete once it has both halves the ticket asks for:
// description and amount.
export function isOtherChargeComplete(line) {
  return Boolean(
    String(line?.description || '').trim() && Number(line?.amount) > 0
  );
}

export function incompleteOtherChargeLines(lines = []) {
  return lines.filter(
    (line) => line.included && isOtherChargeLine(line) && !isOtherChargeComplete(line)
  );
}

export function getInstallmentCohortMonthDate(installment) {
  return installment?.cohortStartDate || installment?.transferDate || null;
}

export function getInstallmentCohortMonthKey(installment) {
  const cohortMonthDate = getInstallmentCohortMonthDate(installment);
  if (!cohortMonthDate) {
    return null;
  }
  return moment(cohortMonthDate).format('YYYY-MM');
}

function formatCohortMonthLabel(cohortMonthDate) {
  if (!cohortMonthDate) {
    return 'Unknown cohort';
  }
  return moment(cohortMonthDate).format('MMMM YYYY');
}

function getAmortizationPeriodIndex(amortization, allAmortizations) {
  const index = allAmortizations.findIndex((item) => item.id === amortization.id);
  return index >= 0 ? index : 0;
}

function getCohortPeriodIndex(installment, amortization, allAmortizations) {
  const cohortDate = getInstallmentCohortMonthDate(installment);
  const periodDate = amortization?.revenueStartDate || amortization?.revenueEndDate;
  if (cohortDate && periodDate) {
    const months = moment(periodDate)
      .startOf('month')
      .diff(moment(cohortDate).startOf('month'), 'months');
    if (Number.isFinite(months)) {
      return Math.max(0, months);
    }
  }
  return getAmortizationPeriodIndex(amortization, allAmortizations);
}

function buildCohortLineId(installmentId, amortization, periodIndex) {
  const amortizationKey = amortization.id ?? `${periodIndex}-${amortization.date}`;
  return `cohort-${installmentId}-${amortizationKey}`;
}

function buildCohortReturnDescription({ installment, periodIndex }) {
  const cohortLabel = formatCohortMonthLabel(getInstallmentCohortMonthDate(installment));
  return `Treisar Cohort Monthly Return — Cohort ${cohortLabel} (M${periodIndex})`;
}

export function deriveRepaymentAmount(amortizationItem = {}) {
  if (amortizationItem.amountToPay != null) {
    return Number(amortizationItem.amountToPay) || 0;
  }
  const predictedRevenueShareAmount =
    Number(amortizationItem.revenueSharePredictedAmount) || 0;
  const minAmount = Number(amortizationItem.minAmount) || 0;
  return minAmount >= predictedRevenueShareAmount
    ? minAmount
    : predictedRevenueShareAmount;
}

export function hasActualRevenueShare(amortizationItem = {}) {
  if (amortizationItem.revenueShareAmount == null) {
    return false;
  }
  return Number(amortizationItem.revenueShareAmount) > 0;
}

function buildFacilityFeeDescription({ paymentNumber, totalPayments }) {
  if (paymentNumber != null && totalPayments != null && totalPayments > 0) {
    return `Facility Fee — Installment ${paymentNumber}/${totalPayments}`;
  }
  return 'Facility Fee';
}

export function getFacilityFeePaymentMeta(facilityFees = [], feeId) {
  const sortedFees = [...facilityFees].sort((a, b) =>
    (a.due_date || '').localeCompare(b.due_date || '')
  );
  const totalPayments = sortedFees.length;
  const paymentIndex = sortedFees.findIndex((fee) => fee.id === feeId);
  if (paymentIndex < 0) {
    return { paymentNumber: null, totalPayments };
  }
  return {
    paymentNumber: paymentIndex + 1,
    totalPayments,
  };
}

function buildFacilityFeeLine(fee, facilityFees) {
  const { paymentNumber, totalPayments } = getFacilityFeePaymentMeta(facilityFees, fee.id);
  return {
    id: `fee-${fee.id}`,
    type: OBLIGATION_TYPE.FACILITY_FEE,
    included: true,
    description: buildFacilityFeeDescription({ paymentNumber, totalPayments }),
    dueDate: fee.due_date,
    amount: fee.amount,
    facilityFeeId: fee.id,
    paymentNumber,
    totalPayments,
    feeComment: fee.comment,
  };
}

function parseObligationDate(date) {
  if (!date) {
    return null;
  }
  if (typeof date === 'string') {
    const parsed = moment(date);
    return parsed.isValid() ? parsed : null;
  }
  if (date.year != null && date.month != null) {
    return moment({
      year: date.year,
      month: date.month - 1,
      day: date.day || 1,
    });
  }
  const parsed = moment(date);
  return parsed.isValid() ? parsed : null;
}

function getInstallmentKey(installment) {
  return installment?.installmentId || installment?.id || null;
}

function isUnpaidAmortization(item) {
  if (item.actualPaidAmount != null) {
    return false;
  }
  if (item.invoiceId || item.invoiceLockedAt) {
    return false;
  }
  if (!hasActualRevenueShare(item)) {
    return false;
  }
  if (item.amountToClose != null) {
    return true;
  }
  return deriveRepaymentAmount(item) > 0;
}

function getInstallmentAmortization(installment) {
  const amortization = installment?.amortization;
  return Array.isArray(amortization) ? amortization : [];
}

function collectUnpaidRepaymentEntries(remainingInstallments = []) {
  const entries = [];
  remainingInstallments.forEach((installment) => {
    const amortizationList = getInstallmentAmortization(installment);
    amortizationList.forEach((amortizationItem) => {
      if (!isUnpaidAmortization(amortizationItem)) {
        return;
      }
      entries.push({ installment, amortizationItem, amortizationList });
    });
  });
  return entries;
}

function collectUnpaidFacilityFees(facilityFees = []) {
  return (facilityFees || []).filter((fee) => fee.status !== 'paid' && fee.due_date);
}

export function getFirstFutureObligationDueDate(
  remainingInstallments = [],
  facilityFees = [],
  today = moment()
) {
  const cohortFutureDueDates = collectUnpaidRepaymentEntries(remainingInstallments)
    .map(({ amortizationItem }) => parseObligationDate(amortizationItem.date))
    .filter((dueDate) => dueDate && dueDate.isAfter(today, 'day'));

  const facilityFutureDueDates = collectUnpaidFacilityFees(facilityFees)
    .map((fee) => parseObligationDate(fee.due_date))
    .filter((dueDate) => dueDate && dueDate.isAfter(today, 'day'));

  const allFutureDueDates = [...cohortFutureDueDates, ...facilityFutureDueDates].sort(
    (a, b) => a.diff(b)
  );

  return allFutureDueDates.length ? allFutureDueDates[0].format('YYYY-MM-DD') : null;
}

export function getFirstFutureRepaymentDueDate(remainingInstallments = [], today = moment()) {
  return getFirstFutureObligationDueDate(remainingInstallments, [], today);
}

export function isRepaymentWithinCutoff(dueDate, cutoffDate, today = moment()) {
  const obligationDueDate = parseObligationDate(dueDate);
  if (!obligationDueDate) {
    return false;
  }
  if (cutoffDate) {
    return obligationDueDate.isSameOrBefore(moment(cutoffDate), 'day');
  }
  return obligationDueDate.isSameOrBefore(today, 'day');
}

function getObligationCutoffDate(
  remainingInstallments = [],
  facilityFees = [],
  today = moment()
) {
  return getFirstFutureObligationDueDate(remainingInstallments, facilityFees, today);
}

function isObligationDueDateSelectable(
  dueDate,
  remainingInstallments = [],
  facilityFees = [],
  today = moment()
) {
  const cutoffDate = getObligationCutoffDate(remainingInstallments, facilityFees, today);
  return isRepaymentWithinCutoff(dueDate, cutoffDate, today);
}

export function pickSuggestedRepaymentEntries(
  remainingInstallments = [],
  facilityFees = [],
  today = moment()
) {
  const cutoffDate = getObligationCutoffDate(remainingInstallments, facilityFees, today);

  return collectUnpaidRepaymentEntries(remainingInstallments)
    .filter(({ amortizationItem }) =>
      isRepaymentWithinCutoff(amortizationItem.date, cutoffDate, today)
    )
    .sort((a, b) =>
      parseObligationDate(a.amortizationItem.date).diff(
        parseObligationDate(b.amortizationItem.date)
      )
    );
}

function pickSuggestedFacilityFees(
  facilityFees = [],
  remainingInstallments = [],
  today = moment()
) {
  const cutoffDate = getObligationCutoffDate(remainingInstallments, facilityFees, today);

  return collectUnpaidFacilityFees(facilityFees)
    .filter((fee) => isRepaymentWithinCutoff(fee.due_date, cutoffDate, today))
    .sort((a, b) => a.due_date.localeCompare(b.due_date));
}

function isCohortRepaymentSelectable(
  amortizationItem,
  remainingInstallments,
  facilityFees = [],
  today = moment()
) {
  if (!isUnpaidAmortization(amortizationItem)) {
    return false;
  }
  return isObligationDueDateSelectable(
    amortizationItem.date,
    remainingInstallments,
    facilityFees,
    today
  );
}

function isFacilityFeeSelectable(fee, remainingInstallments = [], facilityFees = [], today = moment()) {
  if (fee.status === 'paid' || !fee.due_date) {
    return false;
  }
  return isObligationDueDateSelectable(
    fee.due_date,
    remainingInstallments,
    facilityFees,
    today
  );
}

function buildCohortReturnLine(installment, amortization, amortizationList, commitmentLength) {
  const installmentKey = getInstallmentKey(installment);
  const periodIndex = getCohortPeriodIndex(installment, amortization, amortizationList);
  return {
    id: buildCohortLineId(installmentKey, amortization, periodIndex),
    type: OBLIGATION_TYPE.COHORT_RETURN,
    included: true,
    description: buildCohortReturnDescription({
      installment,
      periodIndex,
    }),
    dueDate: amortization.date,
    amount: deriveRepaymentAmount(amortization),
    installmentId: installmentKey,
    amortizationId: amortization.id,
    installmentNumber: installment.installmentNumber,
    commitmentLength,
    cohortMonthKey: getInstallmentCohortMonthKey(installment),
    cohortMonthLabel: formatCohortMonthLabel(getInstallmentCohortMonthDate(installment)),
    periodIndex,
    transferDate: installment.transferDate,
    cohortStartDate: installment.cohortStartDate,
    cohortEndDate: installment.cohortEndDate,
  };
}

export function suggestCohortReturnLines({
  remainingInstallments = [],
  facilityFees = [],
  today = moment(),
  commitmentLength = null,
}) {
  return pickSuggestedRepaymentEntries(remainingInstallments, facilityFees, today).map(
    ({ installment, amortizationItem, amortizationList }) =>
      buildCohortReturnLine(
        installment,
        amortizationItem,
        amortizationList,
        commitmentLength
      )
  );
}

export function suggestFacilityFeeLines({
  facilityFees = [],
  remainingInstallments = [],
  today = moment(),
}) {
  return pickSuggestedFacilityFees(facilityFees, remainingInstallments, today).map((fee) =>
    buildFacilityFeeLine(fee, facilityFees)
  );
}

export function suggestObligationLines({
  remainingInstallments = [],
  facilityFees = [],
  commitmentLength = null,
  today = moment(),
}) {
  const feeLines = suggestFacilityFeeLines({
    facilityFees,
    remainingInstallments,
    today,
  });
  const cohortLines = suggestCohortReturnLines({
    remainingInstallments,
    facilityFees,
    today,
    commitmentLength,
  });
  return [...feeLines, ...cohortLines];
}

export function getAdditionalObligationOptions({
  remainingInstallments = [],
  facilityFees = [],
  existingLineIds = [],
  commitmentLength = null,
  today = moment(),
}) {
  const options = [];

  remainingInstallments.forEach((installment) => {
    const amortization = getInstallmentAmortization(installment);

    amortization.forEach((amortizationItem) => {
      if (
        !isCohortRepaymentSelectable(
          amortizationItem,
          remainingInstallments,
          facilityFees,
          today
        )
      ) {
        return;
      }
      const periodIndex = getCohortPeriodIndex(
        installment,
        amortizationItem,
        amortization
      );
      const id = buildCohortLineId(
        getInstallmentKey(installment),
        amortizationItem,
        periodIndex
      );
      if (existingLineIds.includes(id)) {
        return;
      }
      const dueDateLabel = parseObligationDate(amortizationItem.date);
      options.push({
        value: id,
        label: `${buildCohortReturnDescription({
          installment,
          periodIndex,
        })} — ${dueDateLabel ? dueDateLabel.format('MM/DD/YYYY') : ''}`,
        line: buildCohortReturnLine(
          installment,
          amortizationItem,
          amortization,
          commitmentLength
        ),
      });
    });
  });

  (facilityFees || []).forEach((fee) => {
    if (!isFacilityFeeSelectable(fee, remainingInstallments, facilityFees, today)) {
      return;
    }
    const id = `fee-${fee.id}`;
    if (existingLineIds.includes(id)) {
      return;
    }
    const line = buildFacilityFeeLine(fee, facilityFees);
    options.push({
      value: id,
      label: `${line.description} — ${moment(fee.due_date, 'YYYY-MM-DD').format('MM/DD/YYYY')}`,
      line,
    });
  });

  return options;
}

export function computeDueDateFromLines(lines = []) {
  const included = lines.filter((line) => line.included && line.dueDate);
  if (!included.length) {
    return null;
  }
  return included
    .map((line) => line.dueDate)
    .sort((a, b) => moment(a).diff(moment(b)))[0];
}

export function sumIncludedLineAmounts(lines = []) {
  const included = lines.filter((line) => line.included);
  if (!included.length) {
    return 0;
  }
  // Plain float addition here can leave totalDue with sub-cent noise (e.g.
  // 124139.46 + 10000 -> 134139.46000000002), which then makes an invoice
  // paid in full look forever "partially paid" — see accounting_invoices.go.
  return Decimal.sum(...included.map((line) => Number(line.amount) || 0)).toNumber();
}

export function getLastFundedMonthKey(installments = []) {
  if (!installments.length) {
    return null;
  }
  const latest = installments.reduce((acc, installment) => {
    if (!installment.transferDate) {
      return acc;
    }
    if (!acc || moment(installment.transferDate).isAfter(moment(acc))) {
      return installment.transferDate;
    }
    return acc;
  }, null);
  if (!latest) {
    return null;
  }
  return moment(latest).format('YYYY-MM');
}

export function getNextFundingMonthKey(lastFundedMonthKey) {
  if (!lastFundedMonthKey) {
    return moment().format('YYYY-MM');
  }
  return moment(`${lastFundedMonthKey}-01`).add(1, 'month').format('YYYY-MM');
}

export function formatMonthKeyLabel(monthKey) {
  if (!monthKey) {
    return '';
  }
  return moment(`${monthKey}-01`).format('MMMM YYYY');
}
